// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 09-data-brokering.md
// @greenlight-spec: decisions/0029-principal-scoped-credential-grants.md
import * as React from 'react';
import { User } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { AppIconTile } from '@/components/greenlight/AppIconTile';
import { relativeTime } from '@/components/greenlight/relativeTime';
import { StatusBadge, type StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { HeldReason } from '@/components/permissions/HeldReason';
import { DecisionDialog, type DecisionVerb } from '@/components/permissions/DecisionDialog';
import { api } from '@/lib/api';
import { mutationErrorMessage } from '@/lib/apiErrors';
import type { PermissionListItem } from '@greenlight/shared';

const STATUS_META: Record<string, { variant: StatusBadgeVariant; label: string }> = {
  granted: { variant: 'granted', label: 'Granted' },
  pending: { variant: 'pending', label: 'Pending' },
  denied: { variant: 'failed', label: 'Denied' },
  revoked: { variant: 'revoked', label: 'Revoked' },
};

function PrincipalIdentity({ item }: { item: PermissionListItem }): React.JSX.Element {
  if (item.principal_type === 'user') {
    return (
      <span className="flex min-w-0 items-center gap-2">
        <span
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-gl bg-muted text-muted-foreground"
          role="img"
          aria-label="Person access holder"
        >
          <User className="h-3.5 w-3.5" />
        </span>
        <span className="min-w-0 truncate text-[13px] text-foreground">{item.requested_by}</span>
        <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
          Person
        </span>
      </span>
    );
  }
  return (
    <span className="flex min-w-0 items-center gap-2">
      <AppIconTile
        name={item.app_name ?? 'Unknown app'}
        slug={item.app_slug ?? ''}
        size={24}
        className="shrink-0"
      />
      <span className="min-w-0 truncate text-[13px] text-foreground">
        {item.app_name ?? 'Unknown app'}
      </span>
      <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
        App
      </span>
    </span>
  );
}

/**
 * The credential-centric "who has requested / holds access" ledger (ADR-0029
 * decision 2): every principal — app and user — with a grant row against one
 * credential, with status, requester, and when. Pending and granted rows expose
 * the same grant-id-keyed decision flow as the cross-app Grants sub-view.
 */
export function CredentialAccessLedger({
  integrationId,
  credentialSlug,
}: {
  integrationId: string;
  credentialSlug: string;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [decision, setDecision] = React.useState<{
    verb: DecisionVerb;
    item: PermissionListItem;
  } | null>(null);
  const reviewers = {
    approve: api.permissions.approveById.useMutation(),
    deny: api.permissions.denyById.useMutation(),
    revoke: api.permissions.revokeById.useMutation(),
  };
  const key = ['credential-ledger', integrationId, credentialSlug];
  const { data, isLoading, isError } = api.permissions.queue.useQuery(
    key,
    { query: { integrationId, credentialSlug, limit: 100 } },
    { queryKey: key },
  );

  if (isLoading) {
    return (
      <div className="space-y-2" data-testid={`ledger-loading-${credentialSlug}`}>
        {Array.from({ length: 2 }, (_, i) => (
          <div key={i} className="h-8 w-full rounded-gl bg-muted motion-safe:animate-pulse" />
        ))}
      </div>
    );
  }
  if (isError || data?.status !== 200) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Couldn&apos;t load who has access to this credential.</AlertDescription>
      </Alert>
    );
  }

  const items = data.body.items;
  if (items.length === 0) {
    return (
      <p className="text-[12.5px] text-muted-foreground">
        No app or person has requested this credential yet.
      </p>
    );
  }

  const isPending = decision ? reviewers[decision.verb].isPending : false;

  async function confirmDecision(reason: string | undefined): Promise<void> {
    if (!decision) return;
    const { verb, item } = decision;
    const pastTense = { approve: 'approved', deny: 'denied', revoke: 'revoked' } as const;
    try {
      const response = await reviewers[verb].mutateAsync({
        params: { grantId: item.id },
        body: reason ? { reason } : {},
      });
      if (response.status >= 300) throw response;
      toast.success(`Permission ${pastTense[verb]}.`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['permissions-queue'] }),
        queryClient.invalidateQueries({ queryKey: key }),
        ...(item.app_id !== null
          ? [queryClient.invalidateQueries({ queryKey: ['permissions', item.app_id] })]
          : []),
      ]);
      setDecision(null);
    } catch (error) {
      toast.error(`Couldn't ${verb} permission: ${mutationErrorMessage(error)}`);
    }
  }

  return (
    <>
      <ul
        className="divide-y divide-border rounded-gl border border-border"
        aria-label={`Access ledger for ${credentialSlug}`}
        data-testid={`ledger-${credentialSlug}`}
      >
        {items.map((item) => {
          const meta = STATUS_META[item.status] ?? {
            variant: 'inactive' as StatusBadgeVariant,
            label: item.status,
          };
          return (
            <li
              key={item.id}
              className="flex flex-col gap-2 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
            >
              <span className="min-w-0">
                <PrincipalIdentity item={item} />
                {item.held_reason && <HeldReason reason={item.held_reason} className="mt-1" />}
              </span>
              <span className="flex flex-wrap items-center justify-end gap-2 sm:shrink-0">
                <span className="hidden text-[11.5px] text-muted-foreground md:inline">
                  {item.requested_by}
                  {' · '}
                  <span title={item.requested_at}>{relativeTime(item.requested_at)}</span>
                </span>
                <StatusBadge variant={meta.variant}>{meta.label}</StatusBadge>
                {item.status === 'pending' && (
                  <>
                    <Button
                      variant="destructive-outline"
                      size="sm"
                      onClick={() => setDecision({ verb: 'deny', item })}
                    >
                      Deny
                    </Button>
                    <Button
                      size="sm"
                      disabled={item.held_reason != null}
                      onClick={() => setDecision({ verb: 'approve', item })}
                    >
                      Approve
                    </Button>
                  </>
                )}
                {item.status === 'granted' && (
                  <Button
                    variant="destructive-outline"
                    size="sm"
                    onClick={() => setDecision({ verb: 'revoke', item })}
                  >
                    Revoke
                  </Button>
                )}
              </span>
            </li>
          );
        })}
      </ul>
      <DecisionDialog
        verb={decision?.verb ?? null}
        item={decision?.item ?? null}
        open={decision !== null}
        isPending={isPending}
        onConfirm={(reason) => void confirmDecision(reason)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setDecision(null);
        }}
      />
    </>
  );
}
